import { projectExactLeadCondition } from "pages/campaigns/utils/leadScope";
import { isMaskedUser } from "utils/permission";
import { handleUnauthorized } from "./session";

// Fields the leads LIST actually needs, for the request made on behalf of a
// restricted user. Espo's `select` is a whitelist with no "exclude" form, so
// this has to name everything the list UI and the permission helpers read —
// omitting one silently breaks a column or an edit/delete gate rather than
// erroring. Deliberately absent: phoneNumber, emailAddress, cWhatsapp.
//
// The point is that the contact fields never enter the response, so they are
// not in the Network tab and not in the React Query cache. It is a real
// improvement over masking alone, but it is still NOT access control: the same
// account holds a valid token and can request every field with curl. Only
// field-level read permissions on the Lead entity in EspoCRM close that.
const MASKED_LEAD_SELECT = [
  "id",
  "name",
  "status",
  "source",
  "cSubSource",
  "cProject",
  "cProjectName",
  "cProjectNomen",
  "cNextContactAt",
  "cNextContact",
  "createdAt",
  // Ownership + team scoping — read by isOwnRecord / isTeamRecord to decide
  // whether this user may edit or delete a row.
  "assignedUserId",
  "assignedUserName",
  "createdById",
  "teamsIds",
].join(",");

// cache services?
// Per-user cache namespace. The previous version read
// `localStorage.getItem("userId")` which was never set anywhere in the
// codebase, so every user collapsed onto the same `_guest` bucket and
// inherited the previous session's cached counts on the next login.
// Now reads the real id from `login_object` (set by LoginForm) and
// falls back to `_guest` only when no user is logged in.
const getCacheKey = () => {
  try {
    const userId = JSON.parse(localStorage.getItem("login_object"))?.id;
    return `leads_count_cache_${userId || "guest"}`;
  } catch {
    return "leads_count_cache_guest";
  }
};
// Tiered cache lifetimes. A count whose date window ends BEFORE today is
// historical and barely changes, so it can be cached for hours; a window that
// reaches into today still accumulates new leads, so it must stay short. The
// right TTL is chosen per call by `ttlForFilters` below.
const CACHE_TTL_SHORT = 1000 * 60 * 15; // 15 min — live / current windows
const CACHE_TTL_MEDIUM = 1000 * 60 * 60; // 1 hour — all-time totals (no date bound)
const CACHE_TTL_LONG = 1000 * 60 * 60 * 2; // 2 hours — purely historical windows
const leadsCountCache = new Map();
// 🔥 helpers
const getLocalCache = () => {
  try {
    return JSON.parse(localStorage.getItem(getCacheKey())) || {};
  } catch {
    return {};
  }
};

// Hard ceiling on how many counts we keep. Every distinct filter combination
// produced one permanent entry, and nothing ever removed them: a rep flipping
// through projects, dates, sources and teams generates hundreds in a session,
// all in a single localStorage key that only ever grew.
const MAX_CACHE_ENTRIES = 300;

/**
 * Drop anything that can no longer be served, then the oldest, until the cache
 * is back under the ceiling.
 *
 * Entries older than the longest TTL are dead weight by definition — a reader
 * would re-fetch them anyway — so they go first.
 */
const pruneCache = (cache) => {
  const now = Date.now();
  const entries = Object.entries(cache).filter(
    ([, v]) => v && now - v.timestamp < CACHE_TTL_LONG,
  );
  entries.sort((a, b) => b[1].timestamp - a[1].timestamp);
  return Object.fromEntries(entries.slice(0, MAX_CACHE_ENTRIES));
};

/**
 * Persist one count, and NEVER let the attempt break the caller.
 *
 * This used to be a bare localStorage.setItem with no try/catch and no
 * pruning, which made it a slow-acting outage:
 *
 *   1. the blob grew without limit, one entry per filter combination;
 *   2. eventually the write hit the ~5MB quota and threw QuotaExceededError;
 *   3. the throw happened AFTER a successful network fetch, inside
 *      fetchLeadsCount, so it propagated out and React Query marked the query
 *      failed — the user saw "no data" for a request that had actually
 *      succeeded;
 *   4. refreshing did not help, because storage was still full.
 *
 * And it is shared storage: once full, the LOGIN writes fail too, which is
 * how a full cache turns into "I can't see my data at all".
 *
 * Now: prune first, and if the write still fails, prune hard and retry once;
 * if that also fails, drop the cache entirely. Caching is an optimisation and
 * must never be the reason a number does not appear.
 */
const setLocalCache = (key, value) => {
  const storageKey = getCacheKey();
  const cache = getLocalCache();
  cache[key] = { value, timestamp: Date.now() };

  const write = (payload) =>
    localStorage.setItem(storageKey, JSON.stringify(payload));

  try {
    write(Object.keys(cache).length > MAX_CACHE_ENTRIES ? pruneCache(cache) : cache);
  } catch {
    try {
      // Quota, most likely. Keep only this entry and the freshest few.
      write(pruneCache({ ...cache }));
    } catch {
      try {
        localStorage.removeItem(storageKey);
      } catch {
        /* storage disabled entirely — carry on without a cache */
      }
    }
  }
};

// Relative date types that always reach into "now" (so the count keeps changing
// as leads arrive) vs. ones that are already in the past (fixed once the period
// closed).
const LIVE_DATE_TYPES = new Set(["today", "currentMonth", "lastSevenDays", "after"]);
const PAST_DATE_TYPES = new Set(["yesterday", "lastMonth"]);

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

// Decide how long a given count may be cached. A window is "long-cacheable" only
// when it HAS a date bound and every date bound ends strictly before today —
// e.g. a past month/week/day bucket on the dashboard, which can't change. Any
// window that touches today (or has no date filter at all, i.e. an all-time
// total) still gains new leads, so it stays on the short TTL.
const ttlForFilters = (filters = []) => {
  const todayStart = startOfToday();
  let sawDateFilter = false;
  let allHistorical = true;

  for (const f of filters) {
    const type = f?.type;
    if (LIVE_DATE_TYPES.has(type)) {
      sawDateFilter = true;
      allHistorical = false;
    } else if (PAST_DATE_TYPES.has(type)) {
      sawDateFilter = true;
    } else if (type === "between" && Array.isArray(f.value) && f.value[1]) {
      sawDateFilter = true;
      if (!(new Date(f.value[1]).getTime() < todayStart)) allHistorical = false;
    } else if (type === "before" && f.value) {
      sawDateFilter = true;
      if (!(new Date(f.value).getTime() < todayStart)) allHistorical = false;
    }
    // equals / isNull / source / status clauses don't affect freshness.
  }

  // Past, closed window (e.g. last month) — fixed, cache longest.
  if (sawDateFilter && allHistorical) return CACHE_TTL_LONG;
  // No date bound at all — an all-time total (per-campaign totals, "interested"
  // KPI, etc.). It only creeps up as new leads arrive, so a medium TTL is fine
  // and spares the campaigns page its per-card count fan-out on every visit.
  if (!sawDateFilter) return CACHE_TTL_MEDIUM;
  // A window that reaches into today (today / this week / current month) still
  // gains leads live — keep it short.
  return CACHE_TTL_SHORT;
};

//industry chart
export const fetchLeadsCount = async (filters = []) => {
  // 🔥 Stable cache key — prefixed with the per-user namespace so the
  // in-memory Map can't cross-pollute across logout/login within the
  // same tab. Without the prefix, both users' filter combinations hash
  // to the same key and serve each other's data until the tab reloads.
  const userScope = getCacheKey();
  const cacheKey = `${userScope}::${JSON.stringify(
    filters.map((f) => ({
      ...f,
      value: Array.isArray(f.value) ? [...f.value].sort() : f.value,
    }))
  )}`;

  // Historical windows (past month/week/day) get the long TTL; anything
  // touching today stays short. Same key → same TTL every time (deterministic).
  const ttl = ttlForFilters(filters);

  // ✅ 1. MEMORY CACHE
  const cached = leadsCountCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < ttl) {
    return cached.value;
  }

  // ✅ 2. LOCAL STORAGE CACHE
  const localCache = getLocalCache();
  const localItem = localCache[cacheKey];

  if (localItem && Date.now() - localItem.timestamp < ttl) {
    leadsCountCache.set(cacheKey, localItem);
    return localItem.value;
  }

  // 🔥 3. API CALL
  const token = localStorage.getItem("auth_token");

  const DATE_TYPES = [
    "today",
    "yesterday",
    "currentMonth",
    "lastMonth",
    "lastSevenDays", // ✅ FIXED
    "between",
    "before",
    "after",
  ];

  const query = filters
    .map((f, i) => {
      // Nested OR group — `{ type: "or", value: [ {type,attribute,value}, … ] }`.
      // Serialized as whereGroup[i][value][j][…] for each sub-clause.
      if (f.type === "or" && Array.isArray(f.value)) {
        let q = `whereGroup[${i}][type]=or`;
        f.value.forEach((sub, j) => {
          q += `&whereGroup[${i}][value][${j}][type]=${sub.type}`;
          if (sub.attribute) {
            q += `&whereGroup[${i}][value][${j}][attribute]=${sub.attribute}`;
          }
          if (sub.value != null && sub.value !== "") {
            q += `&whereGroup[${i}][value][${j}][value]=${encodeURIComponent(sub.value)}`;
          }
        });
        return q;
      }

      let q = `whereGroup[${i}][type]=${f.type}&whereGroup[${i}][attribute]=${f.attribute}`;

      // ✅ value handling
      if (Array.isArray(f.value)) {
        f.value.forEach((v) => {
          q += `&whereGroup[${i}][value][]=${encodeURIComponent(v)}`;
        });
      } else if (f.value != null && f.value !== "") {
        q += `&whereGroup[${i}][value]=${encodeURIComponent(f.value)}`;
      }

      // ✅ dateTime flag (based on backend support)
      if (DATE_TYPES.includes(f.type)) {
        q += `&whereGroup[${i}][dateTime]=true`;
      }

      return q;
    })
    .join("&");

  const url = `https://gateway.aajneetiadvertising.com/Lead?${query}&maxSize=1`;

  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      token,
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    console.error("❌ API Error:", res.status, errorText);

    if (res.status === 401) {
      handleUnauthorized();
    }

    throw new Error("Failed to fetch leads count");
  }

  const data = await res.json();
  const total = data.total || 0;

  // ✅ SAVE CACHE
  const cacheEntry = {
    value: total,
    timestamp: Date.now(),
  };

  leadsCountCache.set(cacheKey, cacheEntry);
  setLocalCache(cacheKey, total);

  return total;
};

export const fetchLeads = async ({ limit = 100, page = 1 }) => {
  const token = localStorage.getItem("auth_token");

  const offset = (page - 1) * limit; // ✅ FIX

  const res = await fetch(
    `https://gateway.aajneetiadvertising.com/Lead?maxSize=${limit}&offset=${offset}&orderBy=createdAt&order=desc`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        token,
      },
    }
  );

  if (!res.ok) {
    if (res.status === 401) {
      handleUnauthorized();
    }
    // Carry the status ON the error. Callers were throwing away the only fact
    // that makes a failure diagnosable: a toast reading "Failed to load" is
    // identical whether the session expired (401), the role is not allowed
    // (403), the gateway is rate-limiting (429) or it fell over (5xx), and
    // every one of those needs a different fix. Now the message a user screen-
    // shots names the status.
    const err = new Error(`Failed to fetch leads (HTTP ${res.status})`);
    err.status = res.status;
    throw err;
  }

  return await res.json();
};
// EspoCRM has no native "yesterday" date type, so the deals date filter
// translates it into a whole-day `on` filter for yesterday's calendar day
// (Espo matches the full day for a dateTime field, in the user's timezone).
// Computed in LOCAL time (sv-SE → YYYY-MM-DD) so IST users past 5:30 AM UTC
// don't slip a day the way toISOString() would.
const yesterdayLocalDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toLocaleDateString("sv-SE");
};

export const fetchNewLeads = async ({
  limit,
  page,
  filters = {},
  orderBy = "createdAt",
  order = "desc",
  // Optional column whitelist. The export uses it to pull only what the CSV
  // writes; the table passes nothing and gets the full record as before.
  select = null,
}) => {
  const token = localStorage.getItem("auth_token");
  const offset = (page - 1) * limit;

  let where = [];


  // ✅ DATE FILTER
  if (filters.dateType) {
    const type = filters.dateType;

    switch (type) {
      // ✅ simple types
      case "today":
      case "lastSevenDays":
      case "currentMonth":
      case "lastMonth":
      case "nextMonth":
      case "currentQuarter":
      case "lastQuarter":
      case "currentYear":
      case "lastYear":
      case "past":
      case "future":
      case "ever":
      case "isEmpty":
        where.push({
          type,
          attribute: "createdAt",
          dateTime: true,
        });
        break;

      case "yesterday":
        // No native Espo "yesterday" — express it as a whole-day `on` filter.
        where.push({
          type: "on",
          attribute: "createdAt",
          value: yesterdayLocalDate(),
          dateTime: true,
        });
        break;

      case "on":
      case "before":
      case "after":
        if (filters.closeDateFrom) {
          where.push({
            type,
            attribute: "createdAt",
            value: filters.closeDateFrom,
            dateTime: true,
          });
        }
        break;

      case "between":
        if (filters.closeDateFrom && filters.closeDateTo) {
          where.push({
            type,
            attribute: "createdAt",
            value: [filters.closeDateFrom, filters.closeDateTo],
            dateTime: true,
          });
        }
        break;

      case "lastXDays":
      case "afterXDays":
        if (filters.xDays) {
          where.push({
            type,
            value: filters.xDays,
          });
        }
        break;
    }
  }

  // ✅ NEXT CONTACT FILTER — mirrors the activity-date filter above but
  // targets `cNextContactAt` instead of `createdAt`, with its own filter keys
  // (`nextContactType` / `nextContactFrom` / `nextContactTo` /
  // `nextContactXDays`) so both filters can be active independently.
  if (filters.nextContactType) {
    const type = filters.nextContactType;

    switch (type) {
      case "today":
      case "lastSevenDays":
      case "currentMonth":
      case "lastMonth":
      case "nextMonth":
      case "currentQuarter":
      case "lastQuarter":
      case "currentYear":
      case "lastYear":
      case "past":
      case "future":
      case "ever":
      case "isEmpty":
        where.push({
          type,
          attribute: "cNextContactAt",
          dateTime: true,
        });
        break;

      case "yesterday":
        // No native Espo "yesterday" — whole-day `on` filter for yesterday.
        where.push({
          type: "on",
          attribute: "cNextContactAt",
          value: yesterdayLocalDate(),
          dateTime: true,
        });
        break;

      case "on":
      case "before":
      case "after":
        if (filters.nextContactFrom) {
          where.push({
            type,
            attribute: "cNextContactAt",
            value: filters.nextContactFrom,
            dateTime: true,
          });
        }
        break;

      case "between":
        if (filters.nextContactFrom && filters.nextContactTo) {
          where.push({
            type,
            attribute: "cNextContactAt",
            value: [filters.nextContactFrom, filters.nextContactTo],
            dateTime: true,
          });
        }
        break;

      case "lastXDays":
      case "afterXDays":
        if (filters.nextContactXDays) {
          where.push({
            type,
            attribute: "cNextContactAt",
            value: filters.nextContactXDays,
            dateTime: true,
          });
        }
        break;
    }
  }

  // ✅ OTHER FILTERS
  //
  // SEARCH — name OR phone OR email, not name alone.
  //
  // Typing a number used to return nothing: the only clause was `like %q%` on
  // `name`, so a rep holding a missed call had to know the caller's name
  // before they could find them.
  //
  // The number is normalised to DIGITS and matched on the last ten of them,
  // because the same lead is stored as "+919876543210", "+91 98765 43210" and
  // "9876543210" depending on where it came from. Matching the typed string
  // verbatim finds only whichever spelling happens to be stored.
  //
  // Restricted users keep name-only search, mirroring the rule already
  // documented on the typeahead below: matching on phone or email would let
  // someone confirm a full number by typing it and seeing a hit, which gives
  // back exactly what the mask withholds.
  if (filters.search) {
    const raw = String(filters.search).trim();
    const digits = raw.replace(/\D/g, "");
    // A number, not a name that happens to contain one: at least 4 digits and
    // nothing but digits and phone punctuation.
    const looksLikePhone = digits.length >= 4 && /^[+\d\s()-]+$/.test(raw);
    const phoneNeedle = digits.slice(-10);

    if (isMaskedUser()) {
      where.push({ type: "like", attribute: "name", value: `%${raw}%` });
    } else {
      const or = [{ type: "like", attribute: "name", value: `%${raw}%` }];

      if (looksLikePhone) {
        or.push({
          type: "like",
          attribute: "phoneNumber",
          value: `%${phoneNeedle}%`,
        });
      } else {
        or.push({
          type: "like",
          attribute: "emailAddress",
          value: `%${raw}%`,
        });
      }

      where.push({ type: "or", value: or });
    }
  }
  // 🔹 PROJECT — a value PICKED from the dropdown carries `cProjectRef` (the
  // CProjects record), which matches EXACTLY so one project's leads can't drag
  // in another whose name merely extends it. Free text typed into the filter
  // has no ref and keeps the loose `contains` behaviour, which is what a
  // hand-typed search should do.
  const projectCondition = filters.cProjectRef
    ? projectExactLeadCondition(filters.cProjectRef)
    : null;
  if (projectCondition) {
    where.push(projectCondition);
  } else if (filters.cProject) {
    where.push({
      type: "like",
      attribute: "cProject",
      value: `%${filters.cProject}%`,
    });
  }

  if (filters.status) {
    // Status is now multi-select — rep can pick e.g. ["Interested",
    // "Follow up"]. EspoCRM exposes an `in` operator that matches any
    // value in the supplied array. Falls back to the legacy `equals`
    // shape if a single string somehow arrives (saved filter from
    // before the multi-select migration).
    if (Array.isArray(filters.status)) {
      if (filters.status.length > 0) {
        where.push({
          type: "in",
          attribute: "status",
          value: filters.status,
        });
      }
    } else {
      where.push({
        type: "equals",
        attribute: "status",
        value: filters.status,
      });
    }
  }


  // 🔹 PREVIOUSLY IN STATUS — `cStatusObj` is the ordered list of statuses a
  // lead has passed through, so this answers "was it ever X" independently of
  // what it is now. Pair it with the `status` filter above for was-X-now-Y:
  // status in [Dead] + previousStatus [Follow up] = nurtured, then written off.
  //
  // `arrayAnyOf` is EspoCRM's operator for array attributes — matches when any
  // listed value appears in the array. The builder below already serialises an
  // array `value` as repeated `value[]` params, which is the exact shape Espo's
  // own UI sends.
  if (Array.isArray(filters.previousStatus) && filters.previousStatus.length > 0) {
    where.push({
      type: "arrayAnyOf",
      attribute: "cStatusObj",
      value: filters.previousStatus,
    });
  }

  // Exclude specific statuses — used by the Leads table to hide the inactive /
  // junk statuses by default (they live in the Inactive Leads section). Kept
  // separate from the include `status` filter above so both can coexist.
  if (Array.isArray(filters.excludeStatus) && filters.excludeStatus.length > 0) {
    where.push({
      type: "notIn",
      attribute: "status",
      value: filters.excludeStatus,
    });
  }

  if (filters.source) {
    where.push({
      // `like` (case-insensitive contains) instead of `equals` — reps fill
      // cSubSource freely, so an exact match misses values like "website" /
      // "Website Form" when the option is "Website". The dropdown label stays
      // "Source" but it queries the cSubSource column.
      type: "like",
      attribute: "cSubSource",
      value: `%${filters.source}%`,
    });
  }
  if (filters.sector) {
    where.push({
      type: "equals",
      attribute: "cSector",
      value: filters.sector,
    });
  }

  if (filters.assignUser) {
    where.push({
      type: "equals",
      attribute: "assignedUserId",
      value: filters.assignUser,
    });
  }

  // ✅ TEAM FILTER — `_teamUserIds` is the internal, derived field set by the
  // deals page from the selected team's members. Translates to `assignedUserId
  // IN [...]`. An empty array means the team has no users → match nothing.
  if (Array.isArray(filters._teamUserIds)) {
    if (filters._teamUserIds.length === 0) {
      where.push({
        type: "equals",
        attribute: "id",
        value: "__no_team_users__",
      });
    } else {
      where.push({
        type: "in",
        attribute: "assignedUserId",
        value: filters._teamUserIds,
      });
    }
  }

  // ✅ QUERY BUILDER (FIXED)
  const query = where
    .map((f, i) => {
      // Nested OR clause — one condition matching any of several sub-clauses
      // on DIFFERENT attributes, so it carries no top-level `attribute`.
      // Serialized as whereGroup[i][value][j][...] per sub-clause, the same
      // shape projects.service.js uses for its multi-field search.
      if (f.type === "or" && Array.isArray(f.value)) {
        let q = `whereGroup[${i}][type]=or`;
        f.value.forEach((sub, j) => {
          q += `&whereGroup[${i}][value][${j}][type]=${sub.type}`;
          if (sub.attribute) {
            q += `&whereGroup[${i}][value][${j}][attribute]=${sub.attribute}`;
          }
          if (sub.value !== undefined && sub.value !== "") {
            q += `&whereGroup[${i}][value][${j}][value]=${encodeURIComponent(sub.value)}`;
          }
        });
        return q;
      }

      let q = `whereGroup[${i}][type]=${f.type}`;

      // 🔥 ALWAYS ensure attribute for safety (backend crash fix)
      const attribute = f.attribute || "createdAt";
      q += `&whereGroup[${i}][attribute]=${attribute}`;

      // value
      if (Array.isArray(f.value)) {
        f.value.forEach((v) => {
          q += `&whereGroup[${i}][value][]=${encodeURIComponent(v)}`;
        });
      } else if (f.value !== undefined && f.value !== "") {
        q += `&whereGroup[${i}][value]=${encodeURIComponent(f.value)}`;
      }

      // 🔥 ensure datetime for date filters
      if (
        f.dateTime ||
        [
          "today",
          "lastSevenDays",
          "currentMonth",
          "lastMonth",
          "between",
          "before",
          "after",
        ].includes(f.type)
      ) {
        q += `&whereGroup[${i}][dateTime]=true`;
      }

      return q;
    })
    .join("&");

  // Whitelist sortable attributes so a stray column key can't produce a bad
  // orderBy the backend rejects. Falls back to createdAt/desc.
  // Whitelisted sort columns. Espo 400s on an orderBy it doesn't recognise, and
  // a 400 here surfaces as an EMPTY TABLE rather than an error — so a column
  // only joins this list once its arrow has been exercised in the browser.
  // `cSubSource` is what the Source column actually renders (`source` is kept
  // for older persisted sortConfig values).
  const SORTABLE = [
    "name",
    "source",
    "cSubSource",
    "status",
    "assignedUserName",
    "createdAt",
  ];
  const safeOrderBy = SORTABLE.includes(orderBy) ? orderBy : "createdAt";
  const safeOrder = order === "asc" ? "asc" : "desc";
  // Restricted users get a narrowed response that simply doesn't carry the
  // contact fields — see MASKED_LEAD_SELECT above for why this isn't a
  // substitute for the EspoCRM role.
  const selectParam = isMaskedUser()
    ? `&select=${encodeURIComponent(MASKED_LEAD_SELECT)}`
    : select
      ? `&select=${encodeURIComponent(select)}`
      : "";

  const baseUrl = `https://gateway.aajneetiadvertising.com/Lead?maxSize=${limit}&offset=${offset}&orderBy=${safeOrderBy}&order=${safeOrder}${selectParam}`;

  const url = query ? `${baseUrl}&${query}` : baseUrl;


  const res = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      token,
    },
  });

  if (!res.ok) {
    // Session is gone — send them to login rather than leaving a dead
    // page. 401 ONLY: a 403 means this role may not read THIS entity,
    // which is routine under per-role ACLs and must not end the session.
    if (res.status === 401) {
      handleUnauthorized();
    }
    console.error("API FAILED:", res.status);
    throw new Error("Failed to fetch leads");
  }

  return await res.json();
};
// Paginated lead search for the combobox. Empty query → most-recent leads.
// Non-empty → OR match across name / phone / email / project (contains).
// Returns { list, total } so callers can drive infinite scroll.
export const searchLeads = async ({ query = "", limit = 15, offset = 0 } = {}) => {
  const token = localStorage.getItem("auth_token");
  const params = new URLSearchParams();
  params.append("maxSize", String(limit));
  params.append("offset", String(offset));
  params.append("orderBy", "createdAt");
  params.append("order", "desc");

  // Restricted users also lose phone/email as SEARCH fields: matching on them
  // would let someone confirm a full number by typing it and seeing a hit,
  // which gives back what the mask withholds.
  const masked = isMaskedUser();

  const q = query.trim();
  if (q) {
    const fields = masked
      ? ["name", "cProject"]
      : ["name", "phoneNumber", "emailAddress", "cProject"];
    params.append("whereGroup[0][type]", "or");
    fields.forEach((f, i) => {
      params.append(`whereGroup[0][value][${i}][type]`, "contains");
      params.append(`whereGroup[0][value][${i}][attribute]`, f);
      params.append(`whereGroup[0][value][${i}][value]`, q);
    });
  }
  params.append(
    "attributeSelect",
    masked
      ? "name,cProject,cProjectName,assignedUserId,assignedUserName,status"
      : "name,phoneNumber,emailAddress,cProject,cProjectName,assignedUserId,assignedUserName,status",
  );

  const res = await fetch(
    `https://gateway.aajneetiadvertising.com/Lead?${params.toString()}`,
    { method: "GET", headers: { "Content-Type": "application/json", token } },
  );

  if (!res.ok) {
    if (res.status === 401) {
      handleUnauthorized();
    }
    throw new Error("Failed to search leads");
  }
  return res.json();
};


// Hard ceiling on one export. Keeps the request count small and bounded: at
// EXPORT_CHUNK_SIZE per request this is at most 5 round trips, however many
// leads are ticked. Selections above it are refused with a message rather than
// silently trimmed, which is how the old page-bound export lost rows without
// telling anyone.
export const EXPORT_SELECTION_LIMIT = 500;

// Ids per request. EspoCRM takes an `in` filter as one query param per value,
// so the URL grows ~41 characters per id; 100 keeps it near 4KB, comfortably
// under the ~8KB that servers and proxies typically cap a request line at.
const EXPORT_CHUNK_SIZE = 100;

// Only the columns the CSV actually writes. Fetching the full record for 500
// leads would pull far more down the wire than the file needs.
const EXPORT_SELECT = [
  "id",
  "name",
  "emailAddress",
  "phoneNumber",
  "status",
  // The table's Source column renders cSubSource (reps fill it freely) and
  // falls back to source; the export asked for `source` alone, so the column
  // came out blank or disagreeing with the screen.
  "source",
  "cSubSource",
  "cProject",
  "cProjectName",
  "assignedUserName",
  // `cNextContactAt` is the real attribute — the table, the filter and the
  // drawer all use it. The export asked for `cNextContact`, which doesn't
  // exist, so Next Contact was empty in every file ever exported.
  "cNextContactAt",
  "createdAt",
].join(",");

/**
 * Fetch the full records for a set of lead ids, so the browser can build the
 * export file from them.
 *
 * This is the fallback for EspoCRM's own export action
 * (POST /Lead/action/export), which 500s on this gateway. If that is ever
 * fixed server-side it is the better route — it builds the file in one query
 * with nothing streaming through the browser — but this works against the
 * plain list endpoint that the rest of the app already uses.
 *
 * Requests run SEQUENTIALLY on purpose. Five parallel queries would be faster
 * by a second or so and is not worth adding a burst of concurrent load to a
 * CRM that is also serving everyone else's list views.
 *
 * Returns the records found. Fewer than requested means some ids no longer
 * exist (deleted since selection) — the caller reports the difference rather
 * than quietly handing over a short file.
 */
// NOT CURRENTLY CALLED — kept on purpose, alongside ExportDialog.jsx. This
// pair implemented "export the first N matching the current filter" before the
// Leads button was reverted to the simpler current-page "Export All". Both are
// working and ready to be wired back up; don't remove as dead code.
// Offered in the Export menu. Kept to round numbers rather than a free input
// so nobody types 50,000 and waits.
export const EXPORT_RANGE_OPTIONS = [100, 250, 500];

/**
 * The first N leads matching the CURRENT filter and sort — "give me the top
 * 500 of what's on screen" without ticking a single checkbox.
 *
 * Deliberately built on `fetchNewLeads`, the exact call the table makes, so
 * the export can't drift from what the user is looking at. Re-implementing the
 * filter translation here is how the two would quietly diverge the next time a
 * filter is added.
 *
 * Pages 200 at a time (3 requests for 500) and runs them sequentially — the
 * whole point of this feature is to be lighter on the CRM than selection was.
 *
 * Caveat worth knowing: `offset` paging over live data can skip or repeat a
 * row if leads are created mid-export, since the default sort is `createdAt
 * desc` and new rows push the window along. Negligible at these sizes, but
 * it's why this isn't the right tool for a full-database dump.
 */
export const fetchLeadsForExport = async ({
  filters = {},
  orderBy = "createdAt",
  order = "desc",
  limit = 100,
  // Called after each page with (fetched, target) so the UI can show real
  // progress rather than a spinner that implies nothing in particular.
  onProgress = null,
}) => {
  const capped = Math.min(limit, EXPORT_SELECTION_LIMIT);
  const PAGE = 200;
  const all = [];

  for (let page = 1; all.length < capped; page += 1) {
    const res = await fetchNewLeads({
      limit: PAGE,
      page,
      filters,
      orderBy,
      order,
      select: EXPORT_SELECT,
    });
    const list = res?.list || [];
    all.push(...list);
    onProgress?.(Math.min(all.length, capped), capped);
    // Short page = no more matching leads, however many were asked for.
    if (list.length < PAGE) break;
  }

  return all.slice(0, capped);
};

export const fetchLeadsByIds = async (ids = []) => {
  if (!ids.length) throw new Error("No leads selected");
  if (ids.length > EXPORT_SELECTION_LIMIT) {
    throw new Error(
      `Select up to ${EXPORT_SELECTION_LIMIT} leads per export (${ids.length} selected)`,
    );
  }

  const token = localStorage.getItem("auth_token");
  const all = [];

  for (let i = 0; i < ids.length; i += EXPORT_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + EXPORT_CHUNK_SIZE);

    const params = new URLSearchParams();
    params.append("maxSize", String(chunk.length));
    params.append("offset", "0");
    params.append("select", EXPORT_SELECT);
    params.append("whereGroup[0][type]", "in");
    params.append("whereGroup[0][attribute]", "id");
    chunk.forEach((id) => params.append("whereGroup[0][value][]", id));

    const res = await fetch(
      `https://gateway.aajneetiadvertising.com/Lead?${params.toString()}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json", token },
      },
    );

    if (!res.ok) {
      if (res.status === 401) {
      handleUnauthorized();
    }
      throw new Error(`Could not load the selected leads (${res.status})`);
    }

    const data = await res.json();
    all.push(...(data?.list || []));
  }

  return all;
};

export const fetchLeadsById = async (id) => {
  const token = localStorage.getItem("auth_token");

  const res = await fetch(`https://gateway.aajneetiadvertising.com/Lead/${id}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      token: token, // ✅ backend expects this
    },
  });
  if (!res.ok) {
    console.log("STATUS:", res.status);
    if (res.status === 401) {
      handleUnauthorized();
    }
    throw new Error("Failed to fetch accounts by id");
  }
  return await res.json();
};

export const createLead = async (payload) => {

  const token = localStorage.getItem("auth_token");
  const res = await fetch("https://gateway.aajneetiadvertising.com/Lead", {
    method: "POST",
    headers: { "Content-Type": "application/json", token: token },

    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    // Session is gone — send them to login rather than leaving a dead
    // page. 401 ONLY: a 403 means this role may not read THIS entity,
    // which is routine under per-role ACLs and must not end the session.
    if (res.status === 401) {
      handleUnauthorized();
    }
    console.error("API ERROR:", text);
    throw new Error("Lead is not created", text);
  }
  // EspoCRM returns array
  return await res.json();
};

export const updateLead = async (id, payload) => {
  const token = localStorage.getItem("auth_token");

  const res = await fetch(
    `https://gateway.aajneetiadvertising.com/Lead/${id}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        token: token,
      },
      body: JSON.stringify(payload),
    }
  );


  console.log("response from contact.service.js", res);
  if (!res.ok) {
    // Session is gone — send them to login rather than leaving a dead
    // page. 401 ONLY: a 403 means this role may not read THIS entity,
    // which is routine under per-role ACLs and must not end the session.
    if (res.status === 401) {
      handleUnauthorized();
    }
    throw new Error(text || "Lead update failed");
  }

  return await res.json();
};

export const deleteLead = async (id) => {
  const token = localStorage.getItem("auth_token");
  const res = await fetch(
    `https://gateway.aajneetiadvertising.com/Lead/${id}`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json", token: token },
    }
  );
  if (!res.ok) {
    // Session is gone — send them to login rather than leaving a dead
    // page. 401 ONLY: a 403 means this role may not read THIS entity,
    // which is routine under per-role ACLs and must not end the session.
    if (res.status === 401) {
      handleUnauthorized();
    }
    throw new Error("Failed to delete contact");
  }
  return res.json();
};
export const bulkDeleteleads = async (ids = []) => {
  return Promise.all(ids.map((id) => deleteLead(id)));
};

// --------------Stream-----------
//fetch by Streams
export const leadStreamById = async (id) => {

  const token = localStorage.getItem("auth_token");

  const res = await fetch(
    `https://gateway.aajneetiadvertising.com/Lead/${id}/stream`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        token: token,
      },
    }
  );

  if (!res.ok) {
    console.log("STATUS:", res.status);
    if (res.status === 401) {
      handleUnauthorized();
    }
    throw new Error("Failed to fetch User's stream");
  }
  return await res.json();
};
export const updateStream = async (id, payload) => {

  const token = localStorage.getItem("auth_token");

  const res = await fetch(
    `https://gateway.aajneetiadvertising.com/Lead/${id}/stream`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        token: token,
      },
    }
  );


  if (!res.ok) {
    console.log("STATUS:", res.status);
    if (res.status === 401) {
      handleUnauthorized();
    }
    throw new Error("Failed to fetch User's stream");
  }
  return await res.json();
};

//delete activity with notes api
export const deleteActivity = async (id) => {
  const token = localStorage.getItem("auth_token");
  const res = await fetch(
    `https://gateway.aajneetiadvertising.com/Note/${id}`,
    {
      method: "DELETE",
      headers: { "Content-Type": "application/json", token: token },
    }
  );
  if (!res.ok) {
    // Session is gone — send them to login rather than leaving a dead
    // page. 401 ONLY: a 403 means this role may not read THIS entity,
    // which is routine under per-role ACLs and must not end the session.
    if (res.status === 401) {
      handleUnauthorized();
    }
    throw new Error("Failed to delete Activity");
  }
  return res.json();
};

//create strean
export const createLeadActivity = async (payload) => {

  const token = localStorage.getItem("auth_token");
  const res = await fetch("https://gateway.aajneetiadvertising.com/Note", {
    method: "POST",
    headers: { "Content-Type": "application/json", token: token },

    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    // Session is gone — send them to login rather than leaving a dead
    // page. 401 ONLY: a 403 means this role may not read THIS entity,
    // which is routine under per-role ACLs and must not end the session.
    if (res.status === 401) {
      handleUnauthorized();
    }
    console.error("API ERROR:", text);
    throw new Error("Activity is not created", text);
  }
  // EspoCRM returns array
  return await res.json();
};

// Meet call related Activities

export const leadActivitesById = async (id) => {

  const token = localStorage.getItem("auth_token");

  const res = await fetch(
    `https://gateway.aajneetiadvertising.com/Activities/Lead/${id}/activities`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        token: token,
      },
    }
  );

  console.log(res);
  if (!res.ok) {
    console.log("STATUS:", res.status);
    if (res.status === 401) {
      handleUnauthorized();
    }
    throw new Error("Failed to fetch User's Activties");
  }
  return await res.json();
};


// fetch task by lead id
export const fetchLeadTask = async (id) => {

  const token = localStorage.getItem("auth_token");

  const res = await fetch(
    `https://gateway.aajneetiadvertising.com/Lead/${id}/tasks`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        token: token,
      },
    }
  );


  if (!res.ok) {
    console.log("STATUS:", res.status);
    if (res.status === 401) {
      handleUnauthorized();
    }
    throw new Error("Failed to fetch task for this lead");
  }
  return await res.json();
};
export const createLeadTask = async (id) => {

  const token = localStorage.getItem("auth_token");

  const res = await fetch(
    `https://gateway.aajneetiadvertising.com/Lead/${id}/tasks`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        token: token,
      },
    }
  );


  if (!res.ok) {
    console.log("STATUS:", res.status);
    if (res.status === 401) {
      handleUnauthorized();
    }
    throw new Error("Failed to fetch task for this lead");
  }
  return await res.json();
};